import { useEffect, useState } from 'react';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { Role } from '@/types/Role';
import { formatCurrency } from '@/utils/formatters';
import { isPercentageMethod, HALF_DAY_METHOD_OPTIONS, buildHalfDayHelpVars } from '@/utils/calculateHalfDayRate';
import { HelpButton } from '@/components/ui/help-button';
import { InlineHelpProvider } from '@/components/ui/inline-help-provider';
import { sectionHeader, sectionUnderline, fieldLabelClass as fieldLabel, fieldSublabelClass, FieldLabel, billingBadgeBase, billingBadgeVariant, SegmentedToggle } from './roleFormStyles';
import { useRoleForm } from './useRoleForm';
import { PrefillHint } from './PrefillHint';
import type { RoleLiveValues } from './types';
import billingStructureMd from './helpsheets/billing_structure.md?raw';
import halfDayBillingMd from './helpsheets/half_day_billing.md?raw';
import halfDayRateMethodMd from './helpsheets/half_day_rate_method.md?raw';

const fieldSublabel = `${fieldSublabelClass} max-w-[220px]`;

interface RoleInlineFormProps {
  role: Role | null; // null = create mode
  roles: Role[]; // for duplicate checking
  lastSavedRole?: Role | null; // pre-fill rate from last added/modified role
  onSave: (role: Role, original?: Role) => void;
  onCancel: () => void;
  onDelete?: (role: Role) => void;
  /** Called on every form change so parent can pass live values to the card header */
  onLiveValuesChange?: (values: RoleLiveValues) => void;
}

export function RoleInlineForm({
  role,
  roles,
  lastSavedRole,
  onSave,
  onCancel,
  onDelete,
  onLiveValuesChange,
}: RoleInlineFormProps) {
  const isCreate = role === null;

  const {
    name, setName, description, setDescription,
    billingStructure, setBillingStructure, rate, setRate,
    allowHalfDay, setAllowHalfDay, halfDayMethod, setHalfDayMethod,
    halfDayMultiplier, setHalfDayMultiplier,
    halfDayFixedRate, setHalfDayFixedRate,
    halfDayFixedDiscount, setHalfDayFixedDiscount,
    errors, setErrors, showSuggestions, setShowSuggestions,
    prefillRoleName, clearPrefill,
    nameInputRef, suggestionsRef, descRef,
    filteredSuggestions, calculatedHalfDayRate, headerHalfDayRate,
    selectSuggestion, ensureHalfDayDefaults, validate, buildRole,
    populateFromRole, resetToDefaults, liveHint, isFormValid,
  } = useRoleForm({ roles, isEdit: !isCreate, originalRole: role, lastSavedRole });

  const [hintShake, setHintShake] = useState(false);

  // In create mode, apply defaults on mount. Edit mode state is already
  // initialized from originalRole in useRoleForm (synchronous, so Framer
  // Motion measures the correct height before the first paint).
  useEffect(() => {
    if (!role) resetToDefaults();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-size description textarea on mount
  useEffect(() => {
    const el = descRef.current;
    if (el && description) {
      el.style.height = 'auto';
      el.style.height = `${Math.max(48, el.scrollHeight)}px`;
    }
  }, [description, descRef]);

  // Report live values to parent so RoleListItem can show them
  useEffect(() => {
    if (onLiveValuesChange && !isCreate) {
      onLiveValuesChange({ name, description, billingStructure, rate, headerHalfDayRate });
    }
  }, [name, description, billingStructure, rate, headerHalfDayRate, onLiveValuesChange, isCreate]);

  const handleSave = () => {
    if (!validate()) return;
    onSave(buildRole(), !isCreate && role ? role : undefined);
  };

  const handleDisabledClick = () => {
    if (isFormValid) return;
    setHintShake(true);
    setTimeout(() => setHintShake(false), 600);
    if (!name.trim()) nameInputRef.current?.focus();
  };

  const handleDelete = () => {
    if (role && onDelete) onDelete(role);
  };

  return (
    <InlineHelpProvider>
      {/* ── Live Header (create mode only — edit mode uses RoleListItem with liveOverrides) ── */}
      {isCreate && <AnimatedCollapse open={!!name}>
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-0.5 px-3 py-2.5 border-b border-border/40 bg-surface-2 dark:bg-transparent motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300">
          {/* Row 1, Col 1: Name */}
          <div className="font-heading text-sm uppercase tracking-wide truncate text-foreground self-center transition-all duration-200">
            {name}
          </div>
          {/* Row 1, Col 2: Rate */}
          <AnimatedCollapse open={!!rate && !!billingStructure}>
            <div className="font-mono text-sm font-bold tabular-nums text-primary-text self-center text-right transition-all duration-150">
              {rate ? formatCurrency(rate, { showCents: false }) : ''}
              <span className="text-[10px] sm:text-[12px] font-normal opacity-60">
                /{billingStructure === 'day_rate' ? 'day' : 'hr'}
              </span>
            </div>
          </AnimatedCollapse>
          {/* Row 1-2, Col 3: Empty spacer matching chevron column width */}
          <div className="row-span-2 w-4" />
          {/* Row 2, Col 1: Description */}
          <p className={cn(
            "text-xs text-muted-foreground truncate self-center",
            !description && "italic"
          )}>
            {description || 'No description'}
          </p>
          {/* Row 2, Col 2: Badges */}
          <div className="flex items-center gap-1 self-center justify-end flex-wrap">
            {billingStructure && (
              <span className={cn(billingBadgeBase, billingBadgeVariant(billingStructure), 'transition-colors duration-200')}>
                {billingStructure === 'day_rate' ? 'Day Rate' : 'Hourly'}
              </span>
            )}
            {headerHalfDayRate && (
              <span className={cn(billingBadgeBase, billingBadgeVariant('day_rate'), 'whitespace-nowrap motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300')}>
                &frac12; {headerHalfDayRate}
              </span>
            )}
          </div>
        </div>
      </AnimatedCollapse>}

      <div className="px-3 py-3 space-y-3">
        {/* ── Name & Description ── */}
        <div className="space-y-3">
          {/* Name */}
          <div className="space-y-1 relative">
            <div className="flex justify-between gap-4" style={{ alignItems: 'center' }}>
              <FieldLabel label="Name" sublabel="Short code or title that clients recognize on invoices" className="shrink-0" />
              <Input
                ref={nameInputRef}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (isCreate) setShowSuggestions(true);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
                }}
                onFocus={() => {
                  if (isCreate) setShowSuggestions(true);
                }}
                placeholder="e.g. A1, Stage Manager"
                autoComplete="off"
                style={{ width: `${Math.max(120, name.length * 8.5 + 24)}px` }}
                className={cn('h-8 text-center', errors.name && 'border-destructive')}
              />
            </div>
            {errors.name && <p className="text-xs text-destructive text-right">{errors.name}</p>}

            {/* Suggestions dropdown */}
            {showSuggestions && filteredSuggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                className="absolute z-50 left-0 right-0 top-full mt-1 max-h-32 overflow-y-auto rounded border bg-popover text-popover-foreground shadow-md"
              >
                {filteredSuggestions.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    disabled={s.isUsed}
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm transition-colors',
                      s.isUsed ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent',
                    )}
                    onMouseDown={
                      s.isUsed
                        ? undefined
                        : (e) => {
                            e.preventDefault();
                            selectSuggestion(s);
                          }
                    }
                  >
                    <span className="font-medium">{s.name}</span>
                    {s.isUsed ? (
                      <span className="ml-2 text-muted-foreground text-xs italic">
                        already added
                      </span>
                    ) : (
                      <span className="ml-2 text-muted-foreground text-xs line-clamp-1">
                        {s.description.slice(0, 80)}...
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          <div className="flex justify-between gap-4" style={{ alignItems: 'center' }}>
            <FieldLabel label="Description" sublabel="Scope of work, responsibilities, or skills you bring to this role" className="shrink-0" />
            <Textarea
              ref={descRef}
              value={description}
              maxLength={532}
              onChange={(e) => {
                setDescription(e.target.value);
                const el = e.target;
                el.style.height = 'auto';
                el.style.height = `${Math.max(48, el.scrollHeight)}px`;
              }}
              placeholder="Role description (optional)"
              rows={1}
              style={{ minHeight: '48px', fontSize: '13px' }}
              className="resize-none flex-1 max-w-sm py-2 overflow-hidden lg:max-h-[96px] lg:overflow-y-auto"
            />
          </div>
        </div>

        {/* ── Section: Billing ── */}
        <div className="space-y-3">
          <div className={cn(sectionHeader, sectionUnderline)}>Billing</div>

          {/* Structure toggle */}
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <label className={cn(fieldLabel)}>Structure</label>
                  <HelpButton fieldName="Billing Structure" content={billingStructureMd} />
                </div>
                <p className={fieldSublabel}>Bill this role by the day or by the hour</p>
              </div>
              <SegmentedToggle
                size="sm"
                options={[
                  { value: 'day_rate' as const, label: 'Day Rate' },
                  { value: 'hourly' as const, label: 'Hourly' },
                ]}
                value={billingStructure}
                onChange={(val) => {
                  setBillingStructure(val);
                  if (val === 'hourly') setAllowHalfDay(false);
                  if (errors.billingStructure)
                    setErrors((prev) => ({ ...prev, billingStructure: '' }));
                }}
              />
            </div>
            {errors.billingStructure && (
              <p className="text-xs text-destructive text-right">{errors.billingStructure}</p>
            )}
          </div>

          {/* Rate */}
          <AnimatedCollapse open={!!billingStructure}>
            <div className="space-y-1 pt-1">
              <div className="flex items-center justify-between gap-4">
                <FieldLabel
                  label={billingStructure === 'day_rate' ? 'Day Rate' : 'Hourly Rate'}
                  sublabel={billingStructure === 'day_rate' ? 'Your base full-day fee' : 'Your base per-hour fee — used for overtime multiplier calculations'}
                  className="shrink-0"
                />
                <NumberInput
                  unit="$"
                  unitPosition="left"
                  decimals={0}
                  size="xs"
                  value={rate ?? ''}
                  onChange={(e) => {
                    const val = e.target.value ? parseFloat(e.target.value) : null;
                    setRate(val);
                    clearPrefill();
                    if (errors.rate) setErrors((prev) => ({ ...prev, rate: '' }));
                  }}
                  min={0}
                  step={1}
                  placeholder="0"
                  className={cn('input-recessed', errors.rate && 'border-destructive')}
                />
              </div>
              <PrefillHint roleName={prefillRoleName} />
              {errors.rate && (
                <p className="text-xs text-destructive text-right">{errors.rate}</p>
              )}
            </div>
          </AnimatedCollapse>

          {/* Half-Day Billing (day_rate only) */}
          <AnimatedCollapse open={billingStructure === 'day_rate'}>
            <div className={cn("space-y-3", allowHalfDay && "border-t border-dashed border-border/70 pt-2 mt-1")}>
              {/* Toggle */}
              <div className="flex items-center justify-between gap-4 pt-2">
                <div>
                  <div className="flex items-center gap-2">
                    <label className={cn(fieldLabel)}>Half-Day Billing</label>
                    <HelpButton fieldName="Half-Day Billing" content={halfDayBillingMd} />
                  </div>
                  <p className={fieldSublabel}>Offer a reduced rate for shorter shifts below the half-day threshold</p>
                </div>
                <Switch
                  checked={allowHalfDay}
                  onCheckedChange={(checked) => {
                    setAllowHalfDay(checked);
                    if (checked) ensureHalfDayDefaults();
                  }}
                />
              </div>

              {/* Half-day details */}
              <AnimatedCollapse open={allowHalfDay}>
                <div className="space-y-3 pt-1">
                  {/* Method selection — 4-column grid */}
                  <div className="space-y-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <label className={cn(fieldLabel)}>Method</label>
                        <HelpButton
                          fieldName="Half-Day Rate Method"
                          content={halfDayRateMethodMd}
                          variables={buildHalfDayHelpVars(rate || 500)}
                        />
                      </div>
                      <p className={fieldSublabel}>How the half-day rate is calculated from your full day rate</p>
                    </div>
                    <div className="grid grid-cols-4 gap-2 !mt-3">
                      {HALF_DAY_METHOD_OPTIONS.map(({ value, label, description }) => (
                        <button
                          key={value}
                          type="button"
                          className={cn(
                            'flex flex-col items-center justify-center rounded border py-2 px-2 cursor-pointer transition-colors text-center',
                            halfDayMethod === value
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:border-muted-foreground/30',
                          )}
                          onClick={() => {
                            const newMethod = value;
                            if (
                              isPercentageMethod(halfDayMethod) &&
                              isPercentageMethod(newMethod) &&
                              halfDayMethod !== newMethod
                            ) {
                              setHalfDayMultiplier(null);
                            }
                            setHalfDayMethod(newMethod);
                          }}
                        >
                          <span className={cn('text-xs font-medium', halfDayMethod === value ? 'text-primary-text' : 'text-foreground')}>{label}</span>
                          <span className={cn('text-[10px] sm:text-[12px] font-mono', halfDayMethod === value ? 'text-primary-text' : 'text-muted-foreground')}>
                            {description}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Parameter input */}
                  {isPercentageMethod(halfDayMethod) && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between gap-4">
                        <FieldLabel
                          label={halfDayMethod === 'pct_discount' ? 'Discount' : 'Rate'}
                          sublabel={halfDayMethod === 'pct_discount' ? 'Percentage taken off your full day rate' : 'Percentage of your full day rate charged for half days'}
                          className="shrink-0"
                        />
                        <NumberInput
                          unit="%"
                          unitPosition="right"
                          size="xs"
                          value={halfDayMultiplier ? Math.round(halfDayMultiplier * 100) : ''}
                          onChange={(e) => {
                            const pct = e.target.value ? parseFloat(e.target.value) : null;
                            setHalfDayMultiplier(pct !== null ? pct / 100 : null);
                            if (errors.halfDayMultiplier)
                              setErrors((prev) => ({ ...prev, halfDayMultiplier: '' }));
                          }}
                          min={1}
                          max={99}
                          step={1}
                          placeholder={halfDayMethod === 'pct_discount' ? '25' : '60'}
                          className={cn(
                            'text-right input-recessed',
                            errors.halfDayMultiplier && 'border-destructive',
                          )}
                        />
                      </div>
                      {errors.halfDayMultiplier && (
                        <p className="text-xs text-destructive text-right">
                          {errors.halfDayMultiplier}
                        </p>
                      )}
                    </div>
                  )}

                  {halfDayMethod === 'fixed' && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between gap-4">
                        <FieldLabel label="Half-Day Rate" sublabel="Fixed dollar amount you charge for a half-day shift" className="shrink-0" />
                        <NumberInput
                          unit="$"
                          unitPosition="left"
                          decimals={0}
                          size="xs"
                          value={halfDayFixedRate ?? ''}
                          onChange={(e) => {
                            setHalfDayFixedRate(
                              e.target.value ? parseFloat(e.target.value) : null,
                            );
                            if (errors.halfDayFixedRate)
                              setErrors((prev) => ({ ...prev, halfDayFixedRate: '' }));
                          }}
                          min={0}
                          step={1}
                          placeholder="0.00"
                          className={cn('input-recessed', errors.halfDayFixedRate && 'border-destructive')}
                        />
                      </div>
                      {errors.halfDayFixedRate && (
                        <p className="text-xs text-destructive text-right">
                          {errors.halfDayFixedRate}
                        </p>
                      )}
                    </div>
                  )}

                  {halfDayMethod === 'fixed_discount' && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between gap-4">
                        <FieldLabel label="Discount Amount" sublabel="Dollar amount subtracted from your full day rate for half days" className="shrink-0" />
                        <NumberInput
                          unit="$"
                          unitPosition="left"
                          decimals={0}
                          size="xs"
                          value={halfDayFixedDiscount ?? ''}
                          onChange={(e) => {
                            setHalfDayFixedDiscount(
                              e.target.value ? parseFloat(e.target.value) : null,
                            );
                            if (errors.halfDayFixedDiscount)
                              setErrors((prev) => ({
                                ...prev,
                                halfDayFixedDiscount: '',
                              }));
                          }}
                          min={0}
                          step={1}
                          placeholder="0.00"
                          className={cn('input-recessed', errors.halfDayFixedDiscount && 'border-destructive')}
                        />
                      </div>
                      {errors.halfDayFixedDiscount && (
                        <p className="text-xs text-destructive text-right">
                          {errors.halfDayFixedDiscount}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Calculated preview */}
                  {halfDayMethod !== 'fixed' && calculatedHalfDayRate && (
                    <div className="bg-primary/5 border border-primary/10 rounded p-2 px-3 flex justify-between items-center mt-2 border-t-dashed">
                      <span className="font-label text-[10px] sm:text-[12px] uppercase text-muted-foreground">
                        Calculated Half-Day Rate
                      </span>
                      <span className="font-mono text-xs font-semibold text-primary-text">
                        {calculatedHalfDayRate}
                      </span>
                    </div>
                  )}
                </div>
              </AnimatedCollapse>
            </div>
          </AnimatedCollapse>
        </div>

        {/* ── Action Row ── */}
        <div className="border-t border-border pt-3 mt-2 flex justify-between items-center relative">
          <div>
            {onDelete && role && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border border-destructive/30 text-destructive hover:bg-destructive/10 active:translate-y-px"
                onClick={handleDelete}
              >
                Delete
              </Button>
            )}
          </div>
          {liveHint && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-[120px]">
              <p className={cn(
                'text-xs text-center transition-colors truncate',
                hintShake ? 'text-destructive motion-safe:animate-[shake_0.4s_ease-in-out]' : 'text-muted-foreground'
              )}>{liveHint}</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="active:translate-y-px" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className={cn('bg-primary text-primary-foreground font-semibold active:translate-y-px', !isFormValid && 'opacity-50')}
              onClick={isFormValid ? handleSave : handleDisabledClick}
            >
              {isCreate ? 'Add Role' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </InlineHelpProvider>
  );
}
